import { BREAKPOINT_VALUES } from "constants/layout";
import { PATHS } from "constants/routes";
import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { getConversations } from "services/redux/selectors/chat";
import { menuCountDataSelector } from "services/redux/selectors/userCommonData";
import { AppConversationType } from "types/chat";
import { UserTabType } from "types/layout";
import { useNavigate } from "react-router-dom";

import { lazy } from "react";

const AnalyticArrowIcon = lazy(() => import("assets/icons/arrow.icon").then(module => ({ default: module.AnalyticArrowIcon })));
const BagIcon = lazy(() =>
  import("assets/icons/bag.icon").then((module) => ({ default: module.BagIcon }))
);
const ChatIcon = lazy(() =>
  import("assets/icons/chat.icon").then((module) => ({ default: module.ChatIcon }))
);
const CircleCheckIcon = lazy(() =>
  import("assets/icons/check.icon").then((module) => ({ default: module.CircleCheckIcon }))
);
const ContactIcon = lazy(() =>
  import("assets/icons/contact.icon").then((module) => ({ default: module.ContactIcon }))
);
const DashBoardIcon = lazy(() =>
  import("assets/icons/dashboard.icon").then((module) => ({ default: module.DashBoardIcon }))
);
const InquiryIcon = lazy(() =>
  import("assets/icons/inquiry.icon").then((module) => ({ default: module.InquiryIcon }))
);
const NoteIcon = lazy(() =>
  import("assets/icons/note.icon").then((module) => ({ default: module.NoteIcon }))
);
const PackageIcon = lazy(() =>
  import("assets/icons/package.icon").then((module) => ({ default: module.PackageIcon }))
);
const SettingIcon = lazy(() =>
  import("assets/icons/setting.icon").then((module) => ({ default: module.SettingIcon }))
);

const LayoutContext = createContext<{
  showFullMenu: boolean;
  onToggleShowFull: () => void;
  showInstruction: boolean;
  setShowInstruction: Dispatch<SetStateAction<boolean>>;
  userTabs: UserTabType[];
  onClickTab: (tab: UserTabType) => void;
}>({
  showFullMenu: false,
  onToggleShowFull: () => {},
  showInstruction: false,
  setShowInstruction: () => {},
  userTabs: [],
  onClickTab: () => {},
});

export const LayoutProvider: FC<{
  children?: ReactNode;
}> = ({ children }) => {
  const menuCountStoreData = useSelector(menuCountDataSelector);
  const conversations: AppConversationType[] =
    useSelector(getConversations) || [];
  const isMobile = window.innerWidth <= BREAKPOINT_VALUES.md;
  const navigate = useNavigate();

  const [showFullMenu, setShowFullMenu] = useState(isMobile ? false : true);
  const [showInstruction, setShowInstruction] = useState(false);

  const totalUnreadCount = useMemo(
    () =>
      conversations.reduce(
        (total, conv) => total + (conv.unreadMessagesCount || 0),
        0
      ),
    [conversations]
  );

  const onClickTab = useCallback(
    (tab: UserTabType) => {
      navigate(tab?.path);
    },
    [navigate]
  );

  const userTabs: UserTabType[] = useMemo(
    () => [
      {
        label: "overview",
        path: PATHS.overview,
        icon: DashBoardIcon,
      },
      {
        label: "messages",
        icon: ChatIcon,
        path: PATHS.messages,
        count: totalUnreadCount,
      },
      {
        label: "inquiries",
        icon: NoteIcon,
        path: PATHS.inquiries,
        count: menuCountStoreData?.count_inquiries,
      },
      {
        label: "myQuotations",
        icon: BagIcon,
        path: PATHS.myQuotations,
      },
      {
        label: "smart_trace",
        icon: InquiryIcon,
        path: PATHS.smartTrace,
      },
      {
        label: "products",
        icon: PackageIcon,
        path: PATHS.products,
        componentPath: "/inquiries",
      },
      {
        label: "analytics",
        icon: AnalyticArrowIcon,
        path: PATHS.analytics,
      },
      {
        label: "contacts",
        icon: ContactIcon,
        path: PATHS.contacts,
      },
      {
        label: "setting",
        icon: SettingIcon,
        path: PATHS.setting,
      },
      {
        label: "membership",
        icon: CircleCheckIcon,
        path: PATHS.supplierMembership,
      },
    ],
    [menuCountStoreData, totalUnreadCount]
  );

  const onToggleShowFull = useCallback(() => {
    setShowFullMenu(!showFullMenu);
  }, [showFullMenu]);

  const contextData = useMemo(
    () => ({
      showFullMenu,
      onToggleShowFull,
      showInstruction,
      setShowInstruction,
      userTabs,
      onClickTab,
    }),
    [
      showFullMenu,
      onToggleShowFull,
      showInstruction,
      setShowInstruction,
      userTabs,
      onClickTab,
    ]
  );

  return (
    <LayoutContext.Provider value={contextData}>
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayout = () => useContext(LayoutContext);
